import { URL } from '../common/constants';
import * as services from '.';
import store from '../store/index';
import { isNullOrWhitespace } from '../utils/string.utils';

export interface IEciImplantsService {
  downloadEciForecastFile(season: string, offices: string[], actions: string[]): Promise<void>;
  downloadEciTemplateGarmentsFile(): Promise<void>;
  downloadEciTemplateShoesFile(): Promise<void>;
  downloadFile(season: string, selectionNumber: string, supplierCode: string, reference: string, style: string): Promise<void>;
  downloadSelfTestingTemplateGarmentsFile(): Promise<void>;
  downloadSelfTestingTemplateShoesFile(): Promise<void>;
  uploadFiles(files: FileList, testType: string, userId: string): Promise<any>;
  uploadFilesToQueue(files: FileList, testType: string, userId: string): Promise<any>;
  uploadSelfTestingFile(file: File, userId: string, companyId: string): Promise<any>;
}

class EciImplantsService implements IEciImplantsService {
  public constructor(private httpClient: services.IHttpClient) {
  }

  public downloadEciForecastFile(season: string, offices: string[], actions: string[]): Promise<void> {
    const cowLabsApi = store.getState().settingsStore.settings.cowLabsApi;
    const filenameWithoutExtension = `${season}_${offices.join('_')}`;
    const url = `${URL.COWLABSAPI.ECIFORECAST.BASE}/${URL.COWLABSAPI.ECIFORECAST.DOWNLOAD}/${season}/${offices.join(',')}/${actions.join(',')}`;

    return this.httpClient.downloadFile(`${cowLabsApi}${url}`, filenameWithoutExtension);
  }

  public downloadFile(season: string, selectionNumber: string, supplierCode: string, reference: string, style: string): Promise<void> {
    const cowLabsApi = store.getState().settingsStore.settings.cowLabsApi;

    let filenameWithoutExtension = `${season}`;
    let url = `${URL.COWLABSAPI.ECIFORECAST.BASE}/${URL.COWLABSAPI.ECIFORECAST.APPFORM}?season=${season}`;

    if (!isNullOrWhitespace(selectionNumber)) {
      filenameWithoutExtension += `_${selectionNumber}`;
      url += `&selectionNumber=${selectionNumber}`;
    }

    if (!isNullOrWhitespace(supplierCode)) {
      filenameWithoutExtension += `_${supplierCode}`;
      url += `&supplierCode=${supplierCode}`;
    }

    if (!isNullOrWhitespace(reference)) {
      filenameWithoutExtension += `_${reference}`;
      url += `&reference=${reference}`;
    }

    if (!isNullOrWhitespace(style)) {
      filenameWithoutExtension += `_${style}`;
      url += `&style=${style}`;
    }

    return this.httpClient.downloadFile(`${cowLabsApi}${url}`, filenameWithoutExtension.replace('.', '-'));
  }

  public uploadFiles(files: FileList, testType: string, userId: string): Promise<any> {
    const cowLabsApi = store.getState().settingsStore.settings.cowLabsApi;
    const url = `${URL.COWLABSAPI.ECIFORECAST.BASE}/${URL.COWLABSAPI.ECIFORECAST.WORKSHEET}`;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]); // 'files' es el nombre del campo
    }

    if (testType !== undefined && testType != null) {
      formData.append('testType', testType);
    }

    formData.append('userId', userId);

    return this.httpClient.postFile(`${cowLabsApi}${url}`, formData);
  }

  public uploadFilesToQueue(files: FileList, testType: string, userId: string): Promise<any> {
    const cowLabsApi = store.getState().settingsStore.settings.cowLabsApi;
    const url = `${URL.COWLABSAPI.ECIFORECAST.BASE}/${URL.COWLABSAPI.ECIFORECAST.WORKSHEETTOQUEUE}`;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]); // 'files' es el nombre del campo
    }

    if (testType !== undefined && testType != null) {
      formData.append('testType', testType);
    }

    formData.append('userId', userId);

    return this.httpClient.postFile(`${cowLabsApi}${url}`, formData);
  }

  public uploadSelfTestingFile(file: File, userId: string, companyId: string): Promise<any> {
    const cowLabsApi = store.getState().settingsStore.settings.cowLabsApi;
    const url = `${URL.COWLABSAPI.ECIFORECAST.BASE}/${URL.COWLABSAPI.ECIFORECAST.SELFTESTING}`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);
    formData.append('companyId', companyId);

    return this.httpClient.postFile(`${cowLabsApi}${url}`, formData);
  }

  public downloadEciTemplateGarmentsFile(): Promise<void> {
    const cowLabsApi = store.getState().settingsStore.settings.cowLabsApi;
    const url = `${URL.COWLABSAPI.ECIFORECAST.BASE}/${URL.COWLABSAPI.ECIFORECAST.DOWNLOADECIGARMENTSTEMPLATE}`;

    return this.httpClient.downloadFile(`${cowLabsApi}${url}`, 'EciGarmentsTemplate');
  }

  public downloadEciTemplateShoesFile(): Promise<void> {
    const cowLabsApi = store.getState().settingsStore.settings.cowLabsApi;
    const url = `${URL.COWLABSAPI.ECIFORECAST.BASE}/${URL.COWLABSAPI.ECIFORECAST.DOWNLOADECISHOESTEMPLATE}`;

    return this.httpClient.downloadFile(`${cowLabsApi}${url}`, 'EciShoesTemplate');
  }

  public downloadSelfTestingTemplateGarmentsFile(): Promise<void> {
    const cowLabsApi = store.getState().settingsStore.settings.cowLabsApi;
    const url = `${URL.COWLABSAPI.ECIFORECAST.BASE}/${URL.COWLABSAPI.ECIFORECAST.DOWNLOADSELFTESTINGGARMENTSTEMPLATE}`;

    return this.httpClient.downloadFile(`${cowLabsApi}${url}`, 'SelfTestingGarmentsTemplate');
  }

  public downloadSelfTestingTemplateShoesFile(): Promise<void> {
    const cowLabsApi = store.getState().settingsStore.settings.cowLabsApi;
    const url = `${URL.COWLABSAPI.ECIFORECAST.BASE}/${URL.COWLABSAPI.ECIFORECAST.DOWNLOADSELFTESTINGSHOESTEMPLATE}`;

    return this.httpClient.downloadFile(`${cowLabsApi}${url}`, 'SelfTestingShoesTemplate');
  }
}

export const eciImplantsService = new EciImplantsService(services.httpClient);
